import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import qs from 'qs';
import compact from '@/utils/compact';
import type { AgentApiSharedJobPostsIndex, AgentApiSharedJobPostsItem } from '@/types/serializers';

export type SharedJobPost = AgentApiSharedJobPostsItem;
export interface SearchQuery {
  // 横断検索キーワード (Issue #2465)
  // title / job_summary / mandatory_requirement / company_name / job_position_name を OpenSearch で検索
  search_query?: string;
  // 会社名フィルタ (部分一致, AND 条件)
  company_name?: string;
  job_position_name?: string;
  min_salary?: string;
  max_salary?: string;
  salary?: string;
  // Issue #2530: 年収未記載（min_salary/max_salary が共に NULL）を結果に含めるフラグ
  salary_undisclosed?: string;
  min_fee_rate?: string;
  max_fee_rate?: string;
  fee_rate?: string;
  min_fee_value?: string;
  max_fee_value?: string;
  fee_value?: string;
  sort: string;
  provider?: string;
  shared_job_post_list_id?: string;
  // 都道府県コード絞り込み（Issue #3394）
  prefecture_codes?: number[];
  // 雇用形態絞り込み（Issue #3216）
  employment_types?: string[];
  employment_type_undisclosed?: string;
  // 候補者年齢が応募可能な求人の絞り込み（Issue #3216）
  age?: string;
  age_undisclosed?: string;
  // 候補者の最終学歴が応募可能な求人の絞り込み（Issue #3216）
  education_level?: string;
  education_level_undisclosed?: string;
  // 未経験可の絞り込み（Issue #3529）
  job_type_inexperienced_ok?: string;
  industry_inexperienced_ok?: string;
}

export default function useSharedJobPosts(searchQuery: SearchQuery) {
  // TData はデフォルトの InfiniteData<AgentApiSharedJobPostsIndex> のままにして
  // data.pages が使えるようにする。TPageParam のみ明示する。
  return useInfiniteQuery<
    AgentApiSharedJobPostsIndex,
    Error,
    InfiniteData<AgentApiSharedJobPostsIndex>,
    [string, SearchQuery],
    string | undefined
  >({
    queryKey: ['/agent/api/shared_job_posts', searchQuery],
    queryFn: async ({ pageParam }) => {
      // cursor が undefined の場合はパラメータを付けず、サーバー側でページ先頭として扱われる
      const params = { search_query: compact(searchQuery) } as Record<string, unknown>;
      if (pageParam) {
        params.cursor = pageParam;
      }
      const query = qs.stringify(params, { arrayFormat: 'brackets' });
      const response = await fetch(`/agent/api/shared_job_posts.json?${query}`);
      if (!response.ok) {
        // 401/403/500 等で HTML/エラー JSON が返るケースをそのまま型キャストしないよう、明示的に throw する。
        // TanStack Query 側で error 状態として扱われる。
        throw new Error(`Failed to fetch shared job posts: ${response.status}`);
      }
      const data: AgentApiSharedJobPostsIndex = await response.json();
      return data;
    },
    getNextPageParam: (lastPage) => {
      // next_cursor が null または is_last_page の場合は終了
      if (lastPage.is_last_page || !lastPage.next_cursor) {
        return undefined;
      }
      return lastPage.next_cursor;
    },
    initialPageParam: undefined as string | undefined,
  });
}
